import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Select,
  Modal,
  Alert,
  Empty,
  Typography,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import apiHelpers from "./data";
import buildColumns from "./columns";
import QuestionDrawer from "./QuestionDrawer";
import { STORES} from "../BlogsTable/stores";
import { triggerNotification } from "../../common/notify";
import { DEFAULT_STORE_LABEL as DEFAULT_STORE, SHARED_STORE_LS_KEY } from "../common/storeDefaults";

const LS_STORE_KEY = SHARED_STORE_LS_KEY;

const STORE_OPTIONS = STORES.map((s) => ({
  value: s.label,
  label: `${s.label} — ${s.name}`,
}));
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const TAG_FILTER_OPTIONS = [
  { value: "all",          label: "All Questions" },
  { value: "with_tags",    label: "Tagged options" },
  { value: "without_tags", label: "Untagged options" },
];

const readInitialFilters = (searchParams) => {
  const validLabels = STORES.map((s) => s.label);
  const lsStore = localStorage.getItem(LS_STORE_KEY);
  const qpStore = searchParams.get("qstore");
  const store_label = validLabels.includes(qpStore)
    ? qpStore
    : validLabels.includes(lsStore)
    ? lsStore
    : DEFAULT_STORE;

  const rawTagFilter = searchParams.get("qtagf");
  return {
    store_label,
    search:     searchParams.get("qsearch") || "",
    has_tag:    searchParams.get("qhtag") || "",
    tag_filter: ["with_tags", "without_tags"].includes(rawTagFilter) ? rawTagFilter : "all",
    page:  Math.max(parseInt(searchParams.get("qpage"), 10) || 1, 1),
    limit: PAGE_SIZE_OPTIONS.includes(parseInt(searchParams.get("qlimit"), 10))
      ? parseInt(searchParams.get("qlimit"), 10)
      : 20,
  };
};

const QuestionsTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => readInitialFilters(searchParams));
  const [searchInput, setSearchInput] = useState(filters.search);
  const [hasTagInput, setHasTagInput] = useState(filters.has_tag);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [drawerState, setDrawerState] = useState({ open: false, mode: "view", id: null });

  const abortRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(LS_STORE_KEY, filters.store_label);
  }, [filters.store_label]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("qstore", filters.store_label);
    if (filters.search) next.set("qsearch", filters.search);
    else next.delete("qsearch");
    if (filters.has_tag) next.set("qhtag", filters.has_tag);
    else next.delete("qhtag");
    if (filters.tag_filter && filters.tag_filter !== "all") next.set("qtagf", filters.tag_filter);
    else next.delete("qtagf");
    next.delete("qactive");
    next.set("qpage", String(filters.page));
    next.set("qlimit", String(filters.limit));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((prev) =>
        prev.search === searchInput ? prev : { ...prev, search: searchInput, page: 1 }
      );
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((prev) =>
        prev.has_tag === hasTagInput ? prev : { ...prev, has_tag: hasTagInput, page: 1 }
      );
    }, 300);
    return () => clearTimeout(id);
  }, [hasTagInput]);

  const fetchData = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setFetching(true);
    setLoadError(null);
    try {
      const { items, pagination: pg } = await apiHelpers.fetchQuestions(filters, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setRows(items);
      setPagination(pg);
    } catch (err) {
      if (controller.signal.aborted) return;
      const mapped = apiHelpers.mapError(err);
      if (mapped.code === "CANCELLED") return;
      setLoadError(mapped);
      setRows([]);
      setPagination({ total: 0, totalPages: 0 });
      triggerNotification({
        type: "error",
        title: "Failed to load questions",
        message: mapped.message,
      });
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setLoading(false);
      setFetching(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
    },
    []
  );

  const updateFilter = (patch) =>
    setFilters((prev) => ({ ...prev, ...patch, page: patch.page ?? 1 }));

  const resetFilters = () => {
    setSearchInput("");
    setHasTagInput("");
    setFilters({
      store_label: DEFAULT_STORE,
      search: "",
      has_tag: "",
      tag_filter: "all",
      page: 1,
      limit: 20,
    });
  };

  const openDrawer = (mode, id) => setDrawerState({ open: true, mode, id });
  const closeDrawer = () => setDrawerState((s) => ({ ...s, open: false }));

  const handleView = useCallback((record) => openDrawer("view", record.id), []);
  const handleEdit = useCallback((record) => openDrawer("edit", record.id), []);

  const handleDelete = useCallback(
    (record) => {
      Modal.confirm({
        title: "Delete Question?",
        icon: <ExclamationCircleOutlined />,
        content: (
          <span>
            Delete <b>{record.title || record.queKey}</b>? All its options will be removed too.
          </span>
        ),
        okText: "Delete",
        okButtonProps: { danger: true },
        onOk: async () => {
          const snapshot = rows;
          setRows((prev) => prev.filter((r) => r.id !== record.id));
          try {
            await apiHelpers.deleteQuestion(record.id);
            triggerNotification({
              type: "success",
              title: "Deleted",
              message: `Question "${record.title}" was deleted.`,
            });
            fetchData();
          } catch (err) {
            setRows(snapshot);
            const mapped = apiHelpers.mapError(err);
            triggerNotification({
              type: "error",
              title: "Delete failed",
              message: mapped.message,
            });
          }
        },
      });
    },
    [rows, fetchData]
  );

  const columns = useMemo(
    () =>
      buildColumns({
        onView: handleView,
        onEdit: handleEdit,
        onDelete: handleDelete,
        page: filters.page,
        limit: filters.limit,
      }),
    [handleView, handleEdit, handleDelete, filters.page, filters.limit]
  );

  const showFullPageLoader = loading && !rows.length;

  return (
    <>
      <div style={{ marginBottom: 12 }} className="TableFilterBox">
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <Space wrap>
            <Select
              value={filters.store_label}
              onChange={(v) => updateFilter({ store_label: v })}
              options={STORE_OPTIONS}
              style={{ width: 220 }}
            />
            <Input
              allowClear
              placeholder="Search by key or title"
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ width: 280 }}
            />
            <Input
              allowClear
              placeholder="Has tag"
              prefix={<SearchOutlined />}
              value={hasTagInput}
              onChange={(e) => setHasTagInput(e.target.value)}
              style={{ width: 160 }}
            />
            <Select
              value={filters.tag_filter}
              onChange={(v) => updateFilter({ tag_filter: v })}
              options={TAG_FILTER_OPTIONS}
              style={{ width: 180 }}
            />
            <Select
              value={filters.limit}
              onChange={(v) => updateFilter({ limit: v })}
              options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }))}
              style={{ width: 120 }}
            />
            <Button icon={<ReloadOutlined />} onClick={resetFilters}>
              Reset
            </Button>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openDrawer("create", null)}>
            New Question
          </Button>
        </Space>
      </div>

      {loadError && !fetching && (
        <Alert
          type="error"
          showIcon
          message={loadError.message}
          action={
            <Button size="small" onClick={fetchData}>
              Retry
            </Button>
          }
          style={{ marginBottom: 12 }}
        />
      )}

      <div
        className="TableBox"
        style={{
          maxHeight: 700,
          overflow: "auto",
          opacity: fetching && !showFullPageLoader ? 0.6 : 1,
          transition: "opacity 0.15s",
        }}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={showFullPageLoader}
          locale={{
            emptyText: (
              <Empty
                description={
                  <span>
                    No questions for <b>{filters.store_label}</b>. Click <b>New Question</b> to create one.
                  </span>
                }
              />
            ),
          }}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total: pagination.total,
            showSizeChanger: false,
            showQuickJumper: true,
            position: ["bottomLeft"],
            showTotal: (total) => (
              <Typography.Text type="secondary">
                {total} result{total === 1 ? "" : "s"} · Page {filters.page} of{" "}
                {pagination.totalPages || 1}
              </Typography.Text>
            ),
            onChange: (page) => setFilters((prev) => ({ ...prev, page })),
          }}
          bordered
          scroll={{ x: 1300, y: 500 }}
        />
      </div>

      <QuestionDrawer
        open={drawerState.open}
        mode={drawerState.mode}
        questionId={drawerState.id}
        defaultStoreLabel={filters.store_label}
        onClose={closeDrawer}
        onSaved={() => {
          closeDrawer();
          fetchData();
        }}
      />
    </>
  );
};

export default QuestionsTable;
